import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

const STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  observing: "secondary",
  healthy: "default",
  rolled_back: "destructive",
  escalated_p1: "destructive",
  manual_resolved: "outline",
};

const STATUS_LABELS: Record<string, string> = {
  observing: "Observando",
  healthy: "Saudável",
  rolled_back: "Revertido",
  escalated_p1: "Escalado P1",
  manual_resolved: "Resolvido manual",
};

export function SentinelLoopFechado() {
  const { data: applies } = useQuery({
    queryKey: ["sentinel-apply-log-recent"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sentinel_apply_log")
        .select("id, change_kind, change_ref, status, applied_at, observation_window_hours, resolved_at")
        .order("applied_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return data;
    },
    refetchInterval: 30_000,
  });

  const { data: rollbacks } = useQuery({
    queryKey: ["sentinel-rollback-recent"],
    queryFn: async () => {
      const { data } = await supabase
        .from("sentinel_rollback_events")
        .select("id, action_taken, metric_name, detected_at")
        .order("detected_at", { ascending: false })
        .limit(5);
      return data ?? [];
    },
    refetchInterval: 30_000,
  });

  const counts = (applies ?? []).reduce<Record<string, number>>((acc, a) => {
    acc[a.status as string] = (acc[a.status as string] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Loop Fechado — Últimos applies
          <Badge variant="outline">{applies?.length ?? 0} eventos</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-5 gap-2 text-center text-sm">
          {(["observing", "healthy", "rolled_back", "escalated_p1", "manual_resolved"] as const).map((s) => (
            <div key={s} className="rounded border border-border p-2">
              <div className="text-2xl font-semibold">{counts[s] ?? 0}</div>
              <div className="text-xs text-muted-foreground">{STATUS_LABELS[s]}</div>
            </div>
          ))}
        </div>

        <div className="space-y-2">
          {(applies ?? []).map((a) => (
            <div key={a.id} className="flex items-center justify-between rounded border border-border p-2 text-sm">
              <div className="flex flex-col">
                <span className="font-medium">{a.change_kind} · {a.change_ref}</span>
                <span className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(a.applied_at as string), { locale: ptBR, addSuffix: true })}
                  {" · "}janela {a.observation_window_hours}h
                </span>
              </div>
              <Badge variant={STATUS_VARIANTS[a.status as string] ?? "outline"}>
                {STATUS_LABELS[a.status as string] ?? a.status}
              </Badge>
            </div>
          ))}
          {(applies?.length ?? 0) === 0 && (
            <div className="text-sm text-muted-foreground">Nenhum apply registrado ainda. Mude uma feature flag para começar.</div>
          )}
        </div>

        {(rollbacks ?? []).length > 0 && (
          <div className="border-t border-border pt-3">
            <div className="mb-2 text-sm font-medium">Rollbacks recentes</div>
            {rollbacks!.map((r) => (
              <div key={r.id} className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{r.metric_name} · {r.action_taken}</span>
                <span>{formatDistanceToNow(new Date(r.detected_at as string), { locale: ptBR, addSuffix: true })}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
